import {
  TypeIcon,
  StatusIcon,
  AssigneeAvatar,
  NameLabel,
  ProgressRateLabel,
} from './ganttSliderStyles'
import { LabelMode } from './useLabelMode'
import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import {
  getWbsItemStatusIconColor,
  getWbsItemStatusLabel,
} from '../../../../../../../../../lib/functions/wbsItem'
import { WbsItemStatus } from '../../../../../../../../../domain/entity/WbsItemEntity'
import {
  ProgressAlertIcon,
  ProgressVarianceDiff,
  calcProgressVariance,
  formatProgressRate,
  formatVarianceDiff,
} from './progressVariance'
import { GanttRowItem } from '../type'

type GanttLabelContentProps = {
  isSummary: boolean
  displayOptions?: GanttDisplayOptions
  rowItem?: GanttRowItem
  progressRate: number
  scheduledProgressRate: number | undefined
  labelMode: LabelMode
  hasCumulation: boolean
  omitProgressRate?: boolean
}

/** ラベル表示対象が1つでもあるかを判定する */
export const hasAnyContent = ({
  isSummary,
  displayOptions,
  rowItem,
  hasCumulation,
}: Pick<
  GanttLabelContentProps,
  'isSummary' | 'displayOptions' | 'rowItem' | 'hasCumulation'
>): boolean => {
  const hasTypeIcon =
    !isSummary &&
    !!displayOptions?.showTypeIcon &&
    !!(rowItem?.iconUrl ?? rowItem?.wbsItemType?.iconUrl)
  const hasStatus =
    !isSummary && !!displayOptions?.showStatus && !!rowItem?.status
  const hasName = !!displayOptions?.showName && !!rowItem?.displayName
  const hasAssignee =
    !isSummary && !!displayOptions?.showAssignee && !!rowItem?.responsible
  const hasProgressRate = !!displayOptions?.showProgressRate && hasCumulation
  return hasTypeIcon || hasStatus || hasName || hasAssignee || hasProgressRate
}

/** ホバー用ツールチップテキストを組み立てる */
export const buildHoverText = ({
  rowItem,
  progressRate,
  scheduledProgressRate,
  hasCumulation,
}: Pick<
  GanttLabelContentProps,
  'rowItem' | 'progressRate' | 'scheduledProgressRate' | 'hasCumulation'
>): string => {
  const progressText = hasCumulation
    ? formatProgressRate(progressRate)
    : undefined
  const variance = hasCumulation
    ? calcProgressVariance(progressRate, scheduledProgressRate)
    : undefined
  const varianceText = variance
    ? `(${formatVarianceDiff(variance.diff)})`
    : undefined
  return [
    rowItem?.status
      ? getWbsItemStatusLabel(rowItem.status as WbsItemStatus)
      : undefined,
    rowItem?.displayName,
    rowItem?.responsible?.name,
    progressText
      ? [progressText, varianceText].filter(Boolean).join(' ')
      : undefined,
  ]
    .filter(Boolean)
    .join(' / ')
}

/** ラベル内のコンテンツ（ステータスアイコン・名前・担当者・進捗率） */
export const GanttLabelContent = ({
  isSummary,
  displayOptions,
  rowItem,
  progressRate,
  scheduledProgressRate,
  labelMode,
  hasCumulation,
  omitProgressRate,
}: GanttLabelContentProps) => {
  const iconUrl = rowItem?.iconUrl ?? rowItem?.wbsItemType?.iconUrl
  const hasTypeIcon = !isSummary && !!displayOptions?.showTypeIcon && !!iconUrl
  const hasStatus =
    !isSummary && !!displayOptions?.showStatus && !!rowItem?.status
  const hasName = !!displayOptions?.showName && !!rowItem?.displayName
  const hasAssignee =
    !isSummary && !!displayOptions?.showAssignee && !!rowItem?.responsible
  const hasProgressRate =
    !omitProgressRate && !!displayOptions?.showProgressRate && hasCumulation

  return (
    <>
      {hasTypeIcon && iconUrl && <TypeIcon src={iconUrl} />}
      {hasStatus && rowItem?.status && (
        <StatusIcon
          color={getWbsItemStatusIconColor(rowItem.status as WbsItemStatus)}
        />
      )}
      {hasName && rowItem?.displayName && (
        <NameLabel inside={labelMode === 'inside'} isSummary={isSummary}>
          {rowItem.displayName}
        </NameLabel>
      )}
      {hasAssignee && rowItem?.responsible && (
        <AssigneeAvatar
          src={rowItem.responsible.iconUrl}
          alt={rowItem.responsible.name}
        >
          {rowItem.responsible.name?.charAt(0)}
        </AssigneeAvatar>
      )}
      {hasProgressRate && (
        <>
          <ProgressRateLabel
            inside={labelMode === 'inside'}
            isSummary={isSummary}
            showSeparator={hasStatus || hasName || hasAssignee}
          >
            <ProgressAlertIcon
              progressRate={progressRate}
              scheduledProgressRate={scheduledProgressRate}
              isSummary={isSummary}
            />
            {formatProgressRate(progressRate)}
          </ProgressRateLabel>
          <ProgressVarianceDiff
            progressRate={progressRate}
            scheduledProgressRate={scheduledProgressRate}
            isSummary={isSummary}
          />
        </>
      )}
    </>
  )
}
